import { takeEvery, put, select, call } from 'redux-saga/effects';
import { Actions, PageStoreProps, showPage } from '../reducer/showPageReducer'
import fetchApi from '../../../utils/api/fetchApi';
import { RtnType, ShowPageProps } from '../../../utilsTs/declareTypes';

/**
 * showPage动作
 * @param args 参数列表
 */
function* showPageAction(...args: any): Generator<any, any, any> {
    try {
        const [extra, action] = args;
        const { isWipe } = extra;
        const { payload } = action;
        // 1. 从 action.payload 中获取 apiId 以及本次传入的数据
        const apiId = payload.apiId;
        const incomingCache = payload.cache?.[apiId] ?? {};
        const incomingData = incomingCache.data ?? {};

        // 2. 读取 Redux 中的当前缓存（旧值）
        const pageStore: PageStoreProps = yield select((state: any) => state.pageStore);
        const cache = pageStore.cache ?? {};
        const cached = cache[apiId] ?? {};

        // 3. 合并字段：优先使用传入的新值，否则保留缓存旧值
        //    这样即使只翻页不传 sendPageData，也不会丢掉已有的 sendPageData
        const current = incomingData.current ?? cached.data?.current;
        const pageSize = incomingData.pageSize ?? cached.data?.pageSize;
        const queryString = incomingData.queryString ?? cached.data?.queryString;
        const orderByString = incomingData.orderByString ?? cached.data?.orderByString;
        const useBak = incomingData.useBak ?? cached.data?.useBak;
        const sendPageData = incomingData.sendPageData ?? cached.data?.sendPageData;
        const fetchUrl = incomingCache.fetchUrl ?? cached.fetchUrl;

        // 4. 构造请求参数
        const query = {
            apiId,
            apiExtend: 'showPage',
            apiData: {
                current,
                pageSize,
                queryString,
                orderByString,
                useBak,
                sendPageData,   // 已确保是最新的值
            },
        };

        // 5. 构建新的缓存对象
        let newCache: PageStoreProps['cache'] = { ...cache };

        if (isWipe === true) {
            // 清空数据分支：仅重置 rows 和 total，保留其他状态
            const oldEntry = newCache[apiId] || { fetchUrl: '' };
            const oldData = oldEntry.data || {};
            newCache[apiId] = {
                ...oldEntry,
                fetchUrl,           // 使用合并后的 fetchUrl
                data: {
                    ...oldData,
                    rows: [],
                    total: 0,
                    // 注意：current、pageSize 等保持不变，不需要重置
                } as ShowPageProps,
                success: undefined,
                message: undefined,
            };
        } else {
            // 正常请求数据分支
            let res: RtnType<ShowPageProps> = {
                success: false,
                data: {
                    rows: [],
                    total: 0,
                } as ShowPageProps,
                message: 'fetchUrl 为空，跳过请求',
            };

            if (fetchUrl) {
                res = yield call(fetchApi.GET, fetchUrl, query);
                if (res?.success === false) {
                    res.data = {
                        ...res.data,
                        rows: [],
                        total: 0,
                    };
                }
            } else {
                console.warn('showPageSaga: fetchUrl 为空，使用空数据');
            }

            const oldData = cached.data || {};
            newCache[apiId] = {
                fetchUrl,
                success: res.success,
                data: {
                    ...oldData,
                    ...res.data,        // rows/total 等以服务端为准
                    // 显式写回本次请求用到的参数，不依赖服务端回显
                    current,
                    pageSize,
                    queryString,
                    orderByString,
                    useBak,
                    sendPageData,
                } as ShowPageProps,
                message: res.message,
            };
        }

        // 6. 将结果写回 Redux
        yield put(showPage({ apiId, cache: newCache }));
    } catch (err: any) {
        console.error('showPageSaga 错误:', err);
    }
};

/** 监视ShowPage操作 */
export function* watchShowPageAction() {
    // 监听清空数据动作，传入 isWipe = true
    yield takeEvery(Actions.wipe.id, showPageAction, { isWipe: true });
    // 监听获取数据动作，传入 isWipe = false
    yield takeEvery(Actions.submit.id, showPageAction, { isWipe: false });
}